import { Group } from 'src/design/atoms/Group';
import { Paper } from 'src/design/atoms/Paper';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { IconX } from '@tabler/icons-react';
import React, { useMemo } from 'react';
import { PostLinkItemDto } from 'src/api/FiretrolApiClient';
import { BasicActionIcon } from 'src/design/atoms/BasicActionIcon';
import { LinkButton } from 'src/design/atoms/LinkButton';

interface PostLinksProps {
  links: PostLinkItemDto[];
  onLinkRemoved: (link: PostLinkItemDto) => void;
  disabled?: boolean;
}

const getRouterlink = (link: PostLinkItemDto) => {
  switch (link.resource) {
    case 'Meetings': {
      return `/jobs/${link.jobId}/meetings/edit/${link.id}`;
    }
    case 'RFIs': {
      return `/jobs/${link.jobId}/rfis/${link.id}`;
    }
    case 'Changes': {
      return `/jobs/${link.jobId}/changes/view/${link.id}`;
    }
    case 'Dailys': {
      return `/jobs/${link.jobId}/dailys/view/${link.id}`;
    }
    default: {
      return '';
    }
  }
};

export const PostLinks = React.memo<PostLinksProps>(
  ({ links, onLinkRemoved, disabled = false }) => {
    const sortedLinks = useMemo(() => {
      return [...links].sort((a, b) => {
        // First sort by resource
        const resourceCompare = a.resource.localeCompare(b.resource);
        if (resourceCompare !== 0) return resourceCompare;

        // Then sort by label
        return a.label.localeCompare(b.label);
      });
    }, [links]);

    if (sortedLinks.length === 0) {
      return <></>;
    }

    return (
      <Stack gap="sm">
        {sortedLinks.map((link) => (
          <Paper key={link.id} withBorder p="xs">
            <Group justify="space-between" wrap="nowrap">
              <Group gap="xs" wrap="nowrap">
                <Text tt="uppercase">
                  {link.resource.slice(0, link.resource.length - 1)}
                </Text>
                <LinkButton
                  to={getRouterlink(link)}
                  variant="subtle"
                  size="sm"
                  target="_blank"
                >
                  {link.label}
                </LinkButton>
              </Group>
              {!disabled && (
                <BasicActionIcon
                  variant="subtle"
                  color="red"
                  onClick={() => onLinkRemoved(link)}
                  size="sm"
                >
                  <IconX size={16} />
                </BasicActionIcon>
              )}
            </Group>
          </Paper>
        ))}
      </Stack>
    );
  },
);
